import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Camera,
  FileText,
  Upload,
  Image,
  Trash2,
  Download,
  Eye,
  Plus,
  Calendar,
  User,
  CheckCircle,
  AlertTriangle,
  Clock,
  File,
  FileImage,
  Layers,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  CalendarDays,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to group progress by day
const groupProgressByDay = (progress) => {
  const groups = {};
  
  progress.forEach((update) => {
    const date = new Date(update.created_at).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    if (!groups[date]) {
      groups[date] = {
        date: date,
        dateObj: new Date(update.created_at),
        updates: [],
        totalPhotos: 0,
        maxProgress: 0,
      };
    }
    
    groups[date].updates.push(update);
    groups[date].totalPhotos += update.photos?.length || 0;
    if (update.progress_percentage > groups[date].maxProgress) {
      groups[date].maxProgress = update.progress_percentage;
    }
  });
  
  // Sort by date descending (newest first)
  return Object.values(groups).sort((a, b) => b.dateObj - a.dateObj);
};

// Calculate day number from project start
const calculateDayNumber = (updateDate, projectStartDate) => {
  if (!projectStartDate) return null;
  const start = new Date(projectStartDate);
  const update = new Date(updateDate);
  const diffTime = Math.abs(update - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1; // Day 1, Day 2, etc.
};

export default function ProjectProgressFiles({ projectId, projectTitle, projectStartDate, canEdit = true, canUpload = true }) {
  const [progress, setProgress] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingProgress, setUploadingProgress] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeTab, setActiveTab] = useState("progress");
  const [expandedDays, setExpandedDays] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Progress form
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressForm, setProgressForm] = useState({
    title: "",
    description: "",
    progress_percentage: 0,
    work_done_today: "",
    issues_encountered: "",
    next_steps: "",
    photos: []
  });
  
  // File form
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [fileForm, setFileForm] = useState({
    file_type: "pdf",
    file_name: "",
    description: "",
    category: "general"
  });
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchProgress();
      fetchFiles();
    }
  }, [projectId]);

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/progress`);
      setProgress(res.data);
      
      // Auto-expand the first (most recent) day
      const grouped = groupProgressByDay(res.data);
      if (grouped.length > 0) {
        setExpandedDays({ [grouped[0].date]: true });
      }
    } catch (err) {
      console.error("Error fetching progress:", err);
    }
  };

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/files`);
      setFiles(res.data);
    } catch (err) {
      console.error("Error fetching files:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const uploadedUrls = [];
    
    try {
      for (const file of files) {
        // Get Cloudinary signature
        const sigRes = await axios.get(`${API}/cloudinary/signature?folder=projects&resource_type=image`);
        const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
        
        // Upload to Cloudinary
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", api_key);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        formData.append("folder", folder);
        
        const cloudinaryRes = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          { method: "POST", body: formData }
        );
        
        const cloudinaryData = await cloudinaryRes.json();
        
        if (cloudinaryData.secure_url) {
          uploadedUrls.push(cloudinaryData.secure_url);
        }
      }
      
      if (uploadedUrls.length > 0) {
        setProgressForm(prev => ({
          ...prev,
          photos: [...prev.photos, ...uploadedUrls]
        }));
        toast.success(`${uploadedUrls.length} photo(s) uploaded`);
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.detail || "Failed to upload photos");
    }
  };

  const removePhoto = (index) => {
    setProgressForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const submitProgress = async () => {
    if (!progressForm.title) {
      toast.error("Title is required");
      return;
    }

    setUploadingProgress(true);
    try {
      await axios.post(`${API}/projects/${projectId}/progress`, {
        project_id: projectId,
        ...progressForm
      });
      toast.success("Progress update posted!");
      setProgressDialogOpen(false);
      setProgressForm({
        title: "",
        description: "",
        progress_percentage: 0,
        work_done_today: "",
        issues_encountered: "",
        next_steps: "",
        photos: []
      });
      fetchProgress();
    } catch (err) {
      toast.error("Failed to post progress update");
    } finally {
      setUploadingProgress(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    setFileForm(prev => ({
      ...prev,
      file_name: file.name,
      file_type: getFileType(file.name)
    }));
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return 'pdf';
    if (['ifc'].includes(ext)) return 'ifc';
    if (['dwg', 'dxf'].includes(ext)) return 'dwg';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    return 'other';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-6 h-6 text-red-500" />;
      case 'ifc': return <Layers className="w-6 h-6 text-blue-500" />;
      case 'image': return <FileImage className="w-6 h-6 text-green-500" />;
      default: return <File className="w-6 h-6 text-gray-500" />;
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setUploadingFile(true);
    try {
      // Get Cloudinary signature - use 'raw' for non-image files like PDFs
      const isImage = selectedFile.type.startsWith('image/');
      const resourceType = isImage ? 'image' : 'raw';
      
      const sigRes = await axios.get(`${API}/cloudinary/signature?folder=projects&resource_type=${resourceType}`);
      const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
      
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("api_key", api_key);
      formData.append("timestamp", timestamp);
      formData.append("signature", signature);
      formData.append("folder", folder);
      
      const cloudinaryRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/${resourceType}/upload`,
        { method: "POST", body: formData }
      );
      
      const cloudinaryData = await cloudinaryRes.json();
      
      if (cloudinaryData.secure_url) {
        await axios.post(`${API}/projects/${projectId}/files`, {
          project_id: projectId,
          file_type: fileForm.file_type,
          file_name: fileForm.file_name,
          file_url: cloudinaryData.secure_url,
          description: fileForm.description,
          category: fileForm.category
        });
        
        toast.success("File uploaded successfully!");
        setFileDialogOpen(false);
        setSelectedFile(null);
        setFileForm({
          file_type: "pdf",
          file_name: "",
          description: "",
          category: "general"
        });
        fetchFiles();
      } else {
        toast.error("Failed to upload file");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (!confirm("Are you sure you want to delete this file?")) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/files/${fileId}`);
      toast.success("File deleted");
      fetchFiles();
    } catch (err) {
      toast.error("Failed to delete file");
    }
  };

  const openPreview = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const toggleDayExpanded = (date) => {
    setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));
  };

  // Group progress by day
  const progressByDay = groupProgressByDay(progress);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs for Files vs Progress */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5 border-white/10">
          <TabsTrigger value="progress" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
            <CalendarDays className="w-4 h-4 mr-2" />
            Daily Progress ({progress.length})
          </TabsTrigger>
          <TabsTrigger value="files" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
            <FileText className="w-4 h-4 mr-2" />
            Files ({files.length})
          </TabsTrigger>
        </TabsList>

        {/* Daily Progress Tab */}
        <TabsContent value="progress">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                <Camera className="w-5 h-5 text-[#23F7DD]" />
                Daily Progress
                {progressByDay.length > 0 && (
                  <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border-[#23F7DD]/50">
                    {progressByDay.length} days logged
                  </Badge>
                )}
              </CardTitle>
              {canEdit && (
                <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                      <Plus className="w-4 h-4 mr-2" />
                      Post Update
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-['Unbounded']">Post Progress Update</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Title *</label>
                        <Input
                          value={progressForm.title}
                          onChange={(e) => setProgressForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Day 3 - Base Installation Complete"
                          className="bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Progress % (0-100)</label>
                        <div className="flex items-center gap-4">
                          <Input
                            type="range"
                            min="0"
                            max="100"
                            value={progressForm.progress_percentage}
                            onChange={(e) => setProgressForm(prev => ({ ...prev, progress_percentage: parseInt(e.target.value) }))}
                            className="flex-1"
                          />
                          <span className="text-[#23F7DD] font-bold w-12">{progressForm.progress_percentage}%</span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Work Done Today</label>
                        <Textarea
                          value={progressForm.work_done_today}
                          onChange={(e) => setProgressForm(prev => ({ ...prev, work_done_today: e.target.value }))}
                          placeholder="Describe what was accomplished today..."
                          className="bg-black/50 border-white/10 text-white"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Issues Encountered</label>
                        <Textarea
                          value={progressForm.issues_encountered}
                          onChange={(e) => setProgressForm(prev => ({ ...prev, issues_encountered: e.target.value }))}
                          placeholder="Any problems or delays..."
                          className="bg-black/50 border-white/10 text-white"
                          rows={2}
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Next Steps</label>
                        <Textarea
                          value={progressForm.next_steps}
                          onChange={(e) => setProgressForm(prev => ({ ...prev, next_steps: e.target.value }))}
                          placeholder="What's planned for tomorrow..."
                          className="bg-black/50 border-white/10 text-white"
                          rows={2}
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Photos</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {progressForm.photos.map((url, index) => (
                            <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden">
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                              >
                                <Trash2 className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#23F7DD]/50">
                          <Camera className="w-5 h-5 text-[#94A3B8]" />
                          <span className="text-[#94A3B8]">Add Photos</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                      
                      <Button
                        onClick={submitProgress}
                        disabled={uploadingProgress}
                        className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                      >
                        {uploadingProgress ? "Posting..." : "Post Update"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {progress.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8]">
                  <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No progress updates yet</p>
                  <p className="text-sm mt-1">Progress updates with photos will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Progress Timeline by Day - Card Style */}
                  {progressByDay.map((dayGroup, dayIndex) => {
                    const dayNumber = calculateDayNumber(dayGroup.dateObj, projectStartDate);
                    const isExpanded = expandedDays[dayGroup.date];
                    
                    return (
                      <div
                        key={dayGroup.date}
                        className={`border rounded-xl overflow-hidden transition-all ${
                          isExpanded 
                            ? "border-[#23F7DD]/50 shadow-lg shadow-[#23F7DD]/10" 
                            : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        {/* Day Card Header - Always Visible */}
                        <div
                          onClick={() => toggleDayExpanded(dayGroup.date)}
                          className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                            isExpanded ? "bg-[#23F7DD]/10" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center ${
                              isExpanded ? "bg-[#23F7DD]/30" : "bg-[#23F7DD]/20"
                            }`}>
                              <span className="text-[#23F7DD] font-bold text-lg">
                                {dayGroup.dateObj.getDate()}
                              </span>
                              <span className="text-[#23F7DD] text-xs uppercase">
                                {dayGroup.dateObj.toLocaleDateString('en', { month: 'short' })}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-lg">
                                {dayNumber ? `Day ${dayNumber}` : dayGroup.dateObj.toLocaleDateString('en', { weekday: 'long' })}
                              </h3>
                              <p className="text-sm text-[#94A3B8]">
                                {dayGroup.dateObj.toLocaleDateString('en', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {/* Summary badges */}
                            <div className="flex items-center gap-2">
                              {dayGroup.totalPhotos > 0 && (
                                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">
                                  <Camera className="w-3 h-3 mr-1" />
                                  {dayGroup.totalPhotos} photo{dayGroup.totalPhotos !== 1 ? 's' : ''}
                                </Badge>
                              )}
                              {dayGroup.maxProgress > 0 && (
                                <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border-[#23F7DD]/50">
                                  {dayGroup.maxProgress}% complete
                                </Badge>
                              )}
                            </div>
                            
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                              isExpanded ? "bg-[#23F7DD]/20" : "bg-white/10"
                            }`}>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-[#23F7DD]" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-[#94A3B8]" />
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Day Content - Expanded */}
                        {isExpanded && (
                          <div className="p-4 space-y-4 bg-black/20 border-t border-white/10">
                            {dayGroup.updates.map((update) => (
                              <div
                                key={update.progress_id}
                                className="p-4 bg-white/5 rounded-xl border border-white/10"
                              >
                                {/* Update Header */}
                                <div className="flex items-start justify-between mb-4">
                                  <div>
                                    <h4 className="text-white font-semibold text-base">{update.title}</h4>
                                    <div className="flex items-center gap-3 text-sm text-[#94A3B8] mt-1">
                                      <span className="flex items-center gap-1">
                                        <User className="w-4 h-4 text-[#23F7DD]" />
                                        {update.reported_by_name}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        {new Date(update.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                  </div>
                                  {update.progress_percentage > 0 && (
                                    <div className="text-right">
                                      <span className="text-2xl font-bold text-[#23F7DD]">{update.progress_percentage}%</span>
                                      <div className="w-24 h-2 bg-white/10 rounded-full mt-1">
                                        <div 
                                          className="h-full bg-gradient-to-r from-[#23F7DD] to-[#4F46E5] rounded-full"
                                          style={{ width: `${update.progress_percentage}%` }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Details Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  {update.work_done_today && (
                                    <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                                      <div className="flex items-center gap-2 text-green-400 mb-2">
                                        <CheckCircle className="w-4 h-4" />
                                        <span className="font-medium text-sm">Work Done</span>
                                      </div>
                                      <p className="text-sm text-white/80">{update.work_done_today}</p>
                                    </div>
                                  )}
                                  
                                  {update.issues_encountered && (
                                    <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                                      <div className="flex items-center gap-2 text-yellow-400 mb-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span className="font-medium text-sm">Issues</span>
                                      </div>
                                      <p className="text-sm text-white/80">{update.issues_encountered}</p>
                                    </div>
                                  )}
                                  
                                  {update.next_steps && (
                                    <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                                      <div className="flex items-center gap-2 text-blue-400 mb-2">
                                        <Clock className="w-4 h-4" />
                                        <span className="font-medium text-sm">Next Steps</span>
                                      </div>
                                      <p className="text-sm text-white/80">{update.next_steps}</p>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Photos Gallery */}
                                {update.photos && update.photos.length > 0 && (
                                  <div className="pt-4 border-t border-white/10">
                                    <p className="text-sm text-[#94A3B8] mb-3 flex items-center gap-2">
                                      <Camera className="w-4 h-4" />
                                      Photos ({update.photos.length})
                                    </p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                      {update.photos.map((url, index) => (
                                        <div 
                                          key={index}
                                          className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                                          onClick={() => setSelectedImage(url)}
                                        >
                                          <img
                                            src={url}
                                            alt={`Photo ${index + 1}`}
                                            className="w-full h-full object-cover transition-transform group-hover:scale-110"
                                          />
                                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <Eye className="w-6 h-6 text-white" />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Full Image Preview Modal */}
              {selectedImage && (
                <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
                  <DialogContent className="bg-black/95 border-white/10 max-w-4xl p-2">
                    <img
                      src={selectedImage}
                      alt="Full size"
                      className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                    />
                  </DialogContent>
                </Dialog>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                Project Files
              </CardTitle>
              {canEdit && (
                <Dialog open={fileDialogOpen} onOpenChange={setFileDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload File
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
                    <DialogHeader>
                      <DialogTitle className="font-['Unbounded']">Upload Project File</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Select File (PDF, IFC, Images)</label>
                        <Input
                          type="file"
                          accept=".pdf,.ifc,.dwg,.dxf,.jpg,.jpeg,.png,.gif,.webp"
                          onChange={handleFileSelect}
                          className="bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      {selectedFile && (
                        <>
                          <div className="p-3 bg-white/5 rounded-lg flex items-center gap-3">
                            {getFileIcon(fileForm.file_type)}
                            <div>
                              <p className="text-white font-medium">{selectedFile.name}</p>
                              <p className="text-xs text-[#94A3B8]">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">Category</label>
                            <select
                              value={fileForm.category}
                              onChange={(e) => setFileForm(prev => ({ ...prev, category: e.target.value }))}
                              className="w-full bg-black/50 border border-white/10 rounded-lg p-2 text-white"
                            >
                              <option value="general">General</option>
                              <option value="plans">Plans & Drawings</option>
                              <option value="contracts">Contracts</option>
                              <option value="photos">Photos</option>
                              <option value="reports">Reports</option>
                              <option value="ifc">IFC Models</option>
                              <option value="scaffold_control">Scaffold Control Reports</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">Description (optional)</label>
                            <Input
                              value={fileForm.description}
                              onChange={(e) => setFileForm(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Brief description..."
                              className="bg-black/50 border-white/10 text-white"
                            />
                          </div>
                        </>
                      )}
                      <Button
                        onClick={uploadFile}
                        disabled={!selectedFile || uploadingFile}
                        className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                      >
                        {uploadingFile ? "Uploading..." : "Upload File"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {files.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8]">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No files uploaded yet</p>
                  <p className="text-sm mt-1">Upload PDF plans, IFC models, or photos</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {files.map((file) => (
                    <div
                      key={file.file_id}
                      className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#23F7DD]/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {getFileIcon(file.file_type)}
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{file.file_name}</p>
                            <p className="text-xs text-[#94A3B8] capitalize">{file.category}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPreview(file.file_url)}
                            className="text-[#94A3B8] hover:text-[#23F7DD]"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <a href={file.file_url} download target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="sm" className="text-[#94A3B8] hover:text-[#23F7DD]">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteFile(file.file_id)}
                              className="text-[#94A3B8] hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {file.description && (
                        <p className="text-sm text-[#94A3B8] mt-2">{file.description}</p>
                      )}
                      <p className="text-xs text-[#475569] mt-2">
                        By {file.uploaded_by_name} • {new Date(file.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Image Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded']">Preview</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="flex items-center justify-center">
              {previewUrl.endsWith('.pdf') ? (
                <iframe src={previewUrl} className="w-full h-[70vh]" title="PDF Preview" />
              ) : (
                <img src={previewUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain" />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
